import { useState, type FormEvent } from "react";
import { MessageSquareText } from "lucide-react";

import {
  listRequisitionClarifications,
  respondRequisitionClarification,
} from "@/lib/api";
import { useData } from "@/lib/app-view";
import { displayDate } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { FieldError } from "@/components/field-error";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type Props = {
  organisationId: string;
  requisitionId: string;
  canRespond: boolean;
  onChanged: () => void;
};

export function RequisitionClarificationThread({
  organisationId,
  requisitionId,
  canRespond,
  onChanged,
}: Props) {
  const [refresh, setRefresh] = useState(0);
  const [requestId, setRequestId] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const { data, error, loading } = useData(
    () => listRequisitionClarifications(organisationId, requisitionId),
    [organisationId, requisitionId, refresh],
  );
  const responses = new Map(
    (data || [])
      .filter((event) => event.event_kind === "response")
      .map((event) => [event.request_event_id, event]),
  );
  const requests = (data || []).filter((event) => event.event_kind === "request");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!requestId) return;
    const message = String(new FormData(event.currentTarget).get("message") || "").trim();
    setBusy(true);
    setActionError("");
    try {
      await respondRequisitionClarification(organisationId, requestId, message);
      setRequestId(undefined);
      setRefresh((value) => value + 1);
      onChanged();
    } catch (reason) {
      setActionError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="rounded-2xl border border-[var(--border)] bg-[var(--surface)] p-5" aria-labelledby="clarification-thread-title">
      <div className="flex items-start gap-3">
        <span className="rounded-xl bg-[var(--surface-secondary)] p-2">
          <MessageSquareText className="size-5" aria-hidden="true" />
        </span>
        <div>
          <p className="eyebrow">DECISION DIALOGUE</p>
          <h2 id="clarification-thread-title" className="mt-1 text-lg font-semibold">Clarification requests</h2>
          <p className="mt-1 text-sm text-[var(--text-secondary)]">
            Questions and responses are append-only evidence; the approval state is not silently changed.
          </p>
        </div>
      </div>

      {loading && <p className="mt-4 text-sm text-[var(--text-secondary)]" role="status">Loading clarification evidence…</p>}
      {error && <p className="mt-4 text-sm text-[var(--error)]" role="alert">Clarification evidence is unavailable: {error}</p>}
      {!loading && !error && !requests.length && (
        <p className="mt-4 rounded-xl bg-[var(--surface-secondary)] p-4 text-sm text-[var(--text-secondary)]">
          No clarification has been requested.
        </p>
      )}
      <div className="mt-4 grid gap-3">
        {requests.map((request) => {
          const response = responses.get(request.id);
          return (
            <article key={request.id} className="rounded-xl border border-[var(--border)] p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <strong className="text-sm">{request.creator?.full_name || "Assigned approver"}</strong>
                  <p className="mt-1 text-sm">{request.message}</p>
                </div>
                <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${response ? "border-[var(--success-border)] bg-[var(--success-soft)] text-[var(--success)]" : "border-[var(--warning-border)] bg-[var(--warning-soft)] text-[var(--warning)]"}`}>
                  {response ? "Answered" : "Response required"}
                </span>
              </div>
              <p className="mt-2 text-xs text-[var(--text-muted)]">{displayDate(request.created_at, true)}</p>
              {response ? (
                <div className="mt-3 rounded-lg bg-[var(--surface-secondary)] p-3 text-sm">
                  <strong>{response.creator?.full_name || "Requester"} responded</strong>
                  <p className="mt-1">{response.message}</p>
                  <p className="mt-2 text-xs text-[var(--text-muted)]">{displayDate(response.created_at, true)}</p>
                </div>
              ) : canRespond ? (
                <Button className="mt-3 min-h-11" size="sm" onClick={() => setRequestId(request.id)}>
                  Respond with evidence
                </Button>
              ) : (
                <p className="mt-3 text-xs font-medium text-[var(--text-secondary)]">
                  Waiting for the requester or tenant administrator.
                </p>
              )}
            </article>
          );
        })}
      </div>

      <Dialog open={Boolean(requestId)} onOpenChange={(open) => !open && setRequestId(undefined)}>
        <DialogContent>
          <DialogHeader
            eyebrow="CLARIFICATION RESPONSE"
            title="Respond to the approver"
            description="Answer the specific question and reference any supporting evidence already attached to the requisition."
          />
          <form className="grid gap-4 p-6" onSubmit={submit}>
            <Label>
              Response
              <Textarea name="message" required minLength={10} maxLength={4000} />
            </Label>
            <FieldError>{actionError}</FieldError>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setRequestId(undefined)}>Cancel</Button>
              <Button type="submit" disabled={busy}>{busy ? "Recording…" : "Record response"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}
